import { motion, type Easing } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Service } from "@/lib/content";
import { Layout, Zap, GraduationCap, Smartphone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const easeOut: Easing = [0.16, 1, 0.3, 1];
// Přehozené ikony a routy podle nového pořadí služeb
const serviceIcons = [Layout, Zap, Smartphone, GraduationCap];
const serviceRoutes = ["/tvorba-webu", "/ai-automatizace", "/tvorba-aplikaci", "/ai-skoleni"];

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: easeOut,
    },
  },
  // Mobile slide-in variants
  hiddenLeft: {
    opacity: 0,
    x: -100,
  },
  hiddenRight: {
    opacity: 0,
    x: 100,
  },
  visibleMobile: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: easeOut,
    },
  },
};

const ServiceCard = ({
  service,
  index,
}: {
  service: Service;
  index: number;
}) => {
  const Icon = serviceIcons[index];
  const route = serviceRoutes[index];
  const cardRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileVisible, setIsMobileVisible] = useState(false);
  const isMobileVisibleRef = useRef(false);

  // Check device type on mount, resize, and hover capability change
  useEffect(() => {
    const hoverQuery = window.matchMedia("(hover: hover)");

    const checkDevice = () => {
      const width = window.innerWidth;
      const hasHover = hoverQuery.matches;

      setIsMobile(width < 768);
      setIsTablet((width >= 768 && width < 1024) || (width >= 1024 && !hasHover));
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    hoverQuery.addEventListener("change", checkDevice);
    return () => {
      window.removeEventListener("resize", checkDevice);
      hoverQuery.removeEventListener("change", checkDevice);
    };
  }, []);

  // Scroll listener for mobile - activate card when fully visible
  useEffect(() => {
    if (!isMobile) return;

    let rafId: number;

    const checkVisibility = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const vpHeight = document.documentElement.clientHeight;
      // Card is fully visible when its top is below the navbar (80px)
      // and its bottom is above the viewport bottom
      const visible = rect.top >= 78 && rect.bottom <= vpHeight + 2;
      if (visible !== isMobileVisibleRef.current) {
        isMobileVisibleRef.current = visible;
        setIsMobileVisible(visible);
      }
    };

    const onScroll = () => {
      rafId = requestAnimationFrame(checkVisibility);
    };

    checkVisibility();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  // Different colors for each service
  const colors = [
    { default: "rgba(0, 212, 255, 0.3)", hover: "#00d4ff", gradient: "rgba(0, 212, 255, 0.3)" },
    { default: "rgba(16, 185, 129, 0.3)", hover: "#10b981", gradient: "rgba(16, 185, 129, 0.3)" },
    { default: "rgba(139, 92, 246, 0.3)", hover: "#8b5cf6", gradient: "rgba(139, 92, 246, 0.3)" },
    { default: "rgba(245, 158, 11, 0.3)", hover: "#f59e0b", gradient: "rgba(245, 158, 11, 0.3)" },
  ];

  const color = colors[index];

  // Determine reveal state
  const isRevealed = isTablet || (isMobile && isMobileVisible);
  const slideDirection = index % 2 === 0 ? "hiddenLeft" : "hiddenRight";

  // Color to use based on state
  const activeColor = isRevealed || isHovered ? color.hover : color.default;
  const textColor = isRevealed || isHovered ? "#ffffff" : "rgba(255, 255, 255, 0.85)";

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      initial={slideDirection}
      whileInView="visibleMobile"
      viewport={{ once: true, margin: "-100px" }}
      whileHover={
        !isMobile && !isTablet
          ? {
              y: -8,
              transition: {
                duration: 0.3,
                ease: "easeOut",
              },
            }
          : undefined
      }
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="w-full h-full"
    >
      <Link to={route} className="group block h-full">
        <div
          className="relative h-full p-8 rounded-[0.3rem] backdrop-blur-md overflow-visible flex flex-col transition-all duration-300"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            border: isHovered ? "1px solid rgba(255,255,255,0.35)" : "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {/* Diagonal gradient from bottom-right - only on hover */}
          <div
            className={`absolute inset-0 rounded-[0.3rem] transition-opacity duration-600 pointer-events-none ${
              isRevealed ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            style={{
              background: `radial-gradient(circle at 100% 100%, ${color.gradient} 0%, transparent 70%)`,
            }}
          />

          {/* Content wrapper */}
          <div className="relative z-10 flex flex-col h-full">
            {/* Icon - FIXED SIZE, LEFT-ALIGNED */}
            <div className="mb-6">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-[0.3rem] border transition-colors duration-300"
                style={{
                  backgroundColor: `${color.hover}15`, // zůstává stejné
                  borderColor: isRevealed || isHovered ? color.hover : `${color.hover}30`, // border se rozzáří na hover
                }}
              >
                <Icon
                  className="w-7 h-7 transition-colors duration-300"
                  style={{
                    color: color.hover, // ikona je vždy plná barva (hover color)
                  }}
                />
              </div>
            </div>

            {/* Title - LEFT-ALIGNED */}
            <h4
              className="font-semibold mb-4 text-2xl text-left"
              style={{
                color: "#ffffff",
                textShadow: "0 2px 12px rgba(0, 0, 0, 0.5)",
                lineHeight: "1.3",
              }}
            >
              {service.title}
            </h4>

            {/* Description */}
            <div className="mb-6">
              <p
                className="leading-relaxed text-base transition-colors duration-300"
                style={{
                  color: textColor,
                  fontWeight: 400,
                  lineHeight: "1.6",
                }}
              >
                {service.description}
              </p>
            </div>

            {/* CTA - Fade in on hover/reveal */}
            <div className="mt-auto">
              <span
                className={`inline-flex items-center text-sm font-medium transition-all duration-300 ${
                  isRevealed ? "opacity-100 gap-3" : "opacity-0 gap-2 group-hover:opacity-100 group-hover:gap-3"
                }`}
                style={{
                  color: activeColor,
                }}
              >
                {service.cta}
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${
                    isRevealed ? "translate-x-1" : "group-hover:translate-x-1"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="sluzby"
      className="relative bg-transparent pt-28 lg:pt-32 pb-24 lg:pb-10 scroll-mt-16 lg:scroll-mt-20"
      style={{ zIndex: 10 }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 lg:mb-20"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
            style={{
              color: "#ffffff",
              textShadow: "0 2px 12px rgba(0, 0, 0, 0.5)",
            }}
          >
            {t.services.title}
          </h2>
          <h3
            className="text-lg lg:text-xl font-normal"
            style={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 500,
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.6)",
              lineHeight: "1.8",
            }}
          >
            {t.services.subtitle}
          </h3>
        </motion.div>

        {/* Desktop Layout (lg and up): 2×2 grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="hidden lg:grid grid-cols-2 gap-6"
        >
          {t.services.items.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </motion.div>

        {/* Tablet & Mobile Layout (below lg) */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {t.services.items.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
